import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { change, Field, reduxForm } from 'redux-form';
import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import { connect, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Fab, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';

import renderSelectField from 'src/shared/components/form/Select';
import renderDateTimePickerField from 'src/shared/components/form/DateTimePicker';
import renderField from 'src/shared/components/form/renderField';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';
import { ORDER_TYPE_DROP_OFF, ORDER_TYPE_PICK_UP, ORDER_TYPE_PICK_N_DROP, MERCHANT_ROLE_ID, ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import { convertUTCToLocal } from 'src/utils/DateUtils';
import PickAndDropOrder from './HubPickAndDropOrder';
import DropOffOrder from './HubDropOffOrder';
import PickUpOrder from './HubPickUpOrder';
import OrderBillingAddorUpdate from './OrderBillingAddorUpdate';
import OrderItemAddorUpdate from './OrderItemAddorUpdate';
import CustomerPrimaryAddressBookDetailsModal from 'src/containers/PnD/Customer/CustomerAddressBook/AddOrUpdate/components/CustomerPrimaryAddressBookDetailsModal';

const required = value => (value !== undefined && value !== null && value !== '') ? undefined : 'Required';

const OrderAddOrUpdateForm = ({ handleSubmit, setItemDetails, isInfo, itemDetails, isId, isHub, setHub, isOrderItemDetails, setOrderItemDetails, dispatch }) => {
    useEffect(() => {
        if (!isId) {
            const fields = ['orderNo', 'orderPriorityInfo', 'requestedDateTimeSlot', 'scheduledDateTimeSlot', 'orderType', 'orderFromAddressTypeInfo', 'merchantInfo', 'collectionPointId', 'hubInfo', 'orderToAddressTypeInfo', 'shortCode', 'address', 'buildingName', 'contactNo', 'postalCode', 'doorNo', 'toFloorNo', 'emiratesName', 'contactPersonName', 'areaName', 'grlCode', 'name', 'toAddress', 'toAddressBuildingName', 'toAddressName', 'toAddressShortCode', 'toAddressContactNumber', 'toAddressPostalCode', 'toAddressDoorNo', 'toAddressFloorNo', 'toAddressContactPersonName', 'toAddressAreaName', 'toAddressGrlCode', 'invoiceNo', 'salesOrderNo', 'orderZoneId', 'orderProcessingAreaRate', 'paymentMethodInfo', 'deliveryChargeBearerInfo', 'orderSalesAmount', 'orderAmount', 'orderTotalAmount', 'orderInstruction', 'numberOfBox_1', 'weightKg_1', 'length_1', 'width_1', 'height_1', 'boxDescription_1'];
            fields.forEach(field => dispatch(change('orderForm', field, "")));
        }
        if (localStorage.getItem("uri") === MERCHANT_ROLE_ID) {
            getMerchantAllHubDetails();
        } else {
            getAllHubDetails();
        }
    }, [dispatch, isId])

    const orderPreferences = useSelector(state => state.orderPreferences);
    const [isPopUpHubCustomer, setPopUpHubCustomer] = useState(false);
    const [isExpressDeliveryMin, setExpressDeliveryMin] = useState(null);
    const [isSameDayDelivery, setSameDayDelivery] = useState(false);
    const [isSelectedOrderType, setSelectedOrderType] = useState(null);
    const [isRedirect, setIsRedirect] = useState(false);

    const getMerchantAllHubDetails = async () => {
        try {
            let hubIds = [];
            const response = await PndRestApi.GetMerchantAssignedHub();
            if (response.status === 200 && response.data.status === 200) {
                response.data.data.forEach(item => {
                    hubIds.push({ "label": `${item.hubTypeDescription} (${item.hubName})`, "value": item.hubId });
                });
                setHub(hubIds);
            } else {
                showNotification("theme-light", "ltr", `SuccessCode: ${response.data.status}`, `Message:${response.data.message}`);
            }
        } catch (error) {
            showNotification("theme-light", "ltr", `SuccessCode: 500`, ERROR_MESSAGES.tryCatchError);
        }
    };

    const getAllHubDetails = async () => {
        try {
            let hubIds = [];
            const response = await PndRestApi.GetAllHubDetails();
            if (response.status === 200 && response.data.status === 200) {
                response.data.data.forEach(item => {
                    if (item.hubId === Number(localStorage.getItem("hi"))) {
                        setExpressDeliveryMin(item.expressDeliveryWithIn);
                    }
                    hubIds.push({ "label": item.hubName, "value": item.hubId });
                });
                setHub(hubIds);
            } else {
                showNotification("theme-light", "ltr", `SuccessCode: ${response.data.status}`, `Message:No Hub Found`);
            }
        } catch (error) {
            showNotification("theme-light", "ltr", "SuccessCode: 500", ERROR_MESSAGES.tryCatchError);
        }
    };

    const mangeOrderPriority = (event) => {
        if (event && event.value) {
            setSameDayDelivery(event.value === 2);
            if (isExpressDeliveryMin && event.value === 1) {
                const now = new Date();
                const addedTime = new Date(now.getTime() + isExpressDeliveryMin * 60000);
                const nextDay = new Date(now);
                nextDay.setHours(24, 0, 0, 0);

                if (addedTime >= nextDay) {
                    showNotification("theme-light", "ltr", `SuccessCode: 205`, `Message:This order is going to next day for delivery.`);
                }
            }
        }
    };

    if (isRedirect) return <Redirect to="/web_app/pnd_order/order_details" />;

    return (
        <Container className="p-0">
            <div className="d-flex align-items-center justify-content-between mb-3 mt-2">
                <div>
                    <h5 className="m-0 fw-bold text-dark">{isInfo ? "Edit Order" : "Create Order"}</h5>
                    <p className="text-muted small mb-0">{isInfo ? "Modify the fields as required." : "Please fill all fields to create a new order"}</p>
                </div>
                <Tooltip title="Back to Orders">
                    <Fab size="small" color="success" onClick={() => setIsRedirect(true)} style={{ backgroundColor: '#32CA73' }}>
                        <ArrowBackIcon />
                    </Fab>
                </Tooltip>
            </div>

            <form className="form" onSubmit={handleSubmit}>
                <Card className="border-0 shadow-sm mb-3">
                    <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-4 text-primary border-bottom pb-2">
                            <AssignmentIcon style={{ fontSize: '1.5rem' }} className="me-2" />
                            <h6 className="m-0 fw-bold">Order Information</h6>
                        </div>

                        <Row className="g-3 mb-2">
                            {isInfo && (
                                <Col lg={2} md={4}>
                                    <div className="mb-2">
                                        <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order No.</label>
                                        <Field name="orderNo" component={renderField} type="text" disabled={true} icon={AssignmentIcon} />
                                    </div>
                                </Col>
                            )}
                            <Col lg={2} md={4}>
                                <div className="mb-2">
                                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Order Type <span className='text-danger'>*</span></label>
                                    <Field
                                        validate={[required]}
                                        name="orderType"
                                        component={renderSelectField}
                                        options={[
                                            orderPreferences?.data?.dropOffEnabled && { value: 1, label: "Drop Off" },
                                            orderPreferences?.data?.pickupEnabled && { value: 2, label: "Pick Up" },
                                            orderPreferences?.data?.pickAndDropEnabled && { value: 3, label: "Pick & Drop" },
                                        ].filter(Boolean)}
                                        placeholder="Select Type"
                                        onChange={event => setSelectedOrderType(event?.value)}
                                        isDisabled={!!isInfo?.orderNo}
                                    />
                                </div>
                            </Col>
                            <Col lg={2} md={4}>
                                <div className="mb-2">
                                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Priority <span className='text-danger'>*</span></label>
                                    <Field validate={[required]} name="orderPriorityInfo" component={renderSelectField} options={[
                                        { value: 1, label: `Express (${isExpressDeliveryMin || 0} Min)` },
                                        { value: 2, label: "Same Day" },
                                        { value: 3, label: "Scheduled" },
                                    ]} placeholder="Select Priority" onChange={mangeOrderPriority} />
                                </div>
                            </Col>
                            <Col lg={3} md={6}>
                                <div className="mb-2">
                                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Requested Time <span className='text-danger'>*</span></label>
                                    <Field name="requestedDateTimeSlot" component={renderDateTimePickerField} validate={[required]} />
                                </div>
                            </Col>
                            <Col lg={3} md={6}>
                                <div className="mb-2">
                                    <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Scheduled Time <span className='text-danger'>*</span></label>
                                    <Field name="scheduledDateTimeSlot" component={renderDateTimePickerField} validate={[required]} />
                                </div>
                            </Col>
                        </Row>

                        {((Number(isSelectedOrderType) === ORDER_TYPE_DROP_OFF) || (Number(isInfo?.orderType) === ORDER_TYPE_DROP_OFF)) && (
                            <div className="mt-3 border-top pt-3">
                                <DropOffOrder isInfo={isInfo} />
                            </div>
                        )}

                        {((Number(isSelectedOrderType) === ORDER_TYPE_PICK_UP) || (Number(isInfo?.orderType) === ORDER_TYPE_PICK_UP)) && (
                            <div className="mt-3 border-top pt-3">
                                <PickUpOrder isInfo={isInfo} dropOffRequiredLaterValue={isInfo?.dropOffRequiredLater} assignedHubForCustomer={isHub} />
                            </div>
                        )}

                        {((Number(isSelectedOrderType) === ORDER_TYPE_PICK_N_DROP) || (Number(isInfo?.orderType) === ORDER_TYPE_PICK_N_DROP)) && (
                            <div className="mt-3 border-top pt-3">
                                <PickAndDropOrder isInfo={isInfo} />
                            </div>
                        )}
                    </CardBody>
                </Card>

                <OrderItemAddorUpdate setItemDetails={setItemDetails} itemDetails={itemDetails} isOrderItemDetails={isOrderItemDetails} isId={isId} setOrderItemDetails={setOrderItemDetails} isInfo={isInfo} />

                <OrderBillingAddorUpdate isInfo={isInfo} />

                {orderPreferences?.data?.orderEditableEnabled && (
                    <div className="d-flex justify-content-center mt-4 mb-5">
                        <Button color="success" type="submit" className="px-4 py-2 rounded-pill shadow-sm" style={{ fontWeight: 600 }}>
                            {isId ? "Update Order" : "Submit Order"}
                        </Button>
                    </div>
                )}
            </form>

            {isPopUpHubCustomer && (
                <CustomerPrimaryAddressBookDetailsModal modalShow={isPopUpHubCustomer} setModalShow={setPopUpHubCustomer} isTitle={"Add New Customer"} />
            )}
        </Container>
    );
};

OrderAddOrUpdateForm.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
};

const mapStateToProps = (state, data) => {
    const prefs = state.orderPreferences?.data || {};
    const enabledTypes = [
        prefs.dropOffEnabled && { value: 1, label: "Drop Off" },
        prefs.pickupEnabled && { value: 2, label: "Pick Up" },
        prefs.pickAndDropEnabled && { value: 3, label: "Pick & Drop" },
    ].filter(Boolean);

    if (data && data?.isInfo) {
        return {
            initialValues: {
                orderId: data.isInfo?.orderId,
                name: { value: data?.isInfo?.fromUniqueId, label: data?.isInfo?.name } || { value: data?.isInfo?.toUniqueId, label: data?.isInfo?.name },
                orderType: data?.isInfo?.orderType ? {
                    value: Number(data.isInfo.orderType),
                    label: Number(data.isInfo.orderType) === 1 ? "Drop Off" : Number(data.isInfo.orderType) === 2 ? "Pick Up" : Number(data.isInfo.orderType) === 3 ? "Pick & Drop" : "Pick Up"
                } : null,
                orderFromAddressTypeInfo: { value: Number(data.isInfo?.orderFromAddressTypeInfo?.orderAddressTypeId), label: Number(data.isInfo?.orderFromAddressTypeInfo?.orderAddressTypeId) === 1 ? "Own Hub" : Number(data.isInfo?.orderFromAddressTypeInfo?.orderAddressTypeId) === 2 ? "Hub Customer" : Number(data.isInfo?.orderFromAddressTypeInfo?.orderAddressTypeId) === 3 ? "Merchant" : Number(data.isInfo?.orderFromAddressTypeInfo?.orderAddressTypeId) === 4 ? "Another Hub" : Number(data.isInfo?.orderFromAddressTypeInfo?.orderAddressTypeId) === 5 ? "Merchant Customer" : null },
                orderToAddressTypeInfo: { value: Number(data.isInfo?.orderToAddressTypeInfo?.orderAddressTypeId), label: Number(data.isInfo?.orderToAddressTypeInfo?.orderAddressTypeId) === 1 ? "Own Hub" : Number(data.isInfo?.orderToAddressTypeInfo?.orderAddressTypeId) === 2 ? "Hub Customer" : Number(data.isInfo?.orderToAddressTypeInfo?.orderAddressTypeId) === 3 ? "Merchant" : Number(data.isInfo?.orderToAddressTypeInfo?.orderAddressTypeId) === 4 ? "Another Hub" : Number(data.isInfo?.orderToAddressTypeInfo?.orderAddressTypeId) === 5 ? "Merchant Customer" : null },
                contactNo: data.isInfo?.contactNo,
                doorNo: data.isInfo?.doorNo,
                postalCode: data.isInfo?.postalCode,
                contactPersonName: data.isInfo?.contactPersonName,
                address: data.isInfo?.address,
                emiratesName: data.isInfo?.emiratesName,
                areaName: data.isInfo?.areaName,
                floorNo: data.isInfo?.floorNo,
                toFloorNo: data.isInfo?.toFloorNo,
                shortCode: data.isInfo?.shortCode,
                grlCode: data.isInfo?.grlCode,
                deliveryChargeBearerInfo: { value: data?.isInfo?.deliveryChargeBearerInfo?.deliveryChargeId, label: data?.isInfo?.deliveryChargeBearerInfo?.deliveryChargeBearerName },
                orderPriorityInfo: (() => {
                    const pId = data?.isInfo?.orderPriorityInfo?.orderPriorityId ?? data?.isInfo?.orderPriorityId;
                    if (!pId) return null;
                    const numPId = Number(pId);
                    const pLabel = numPId === 1 ? "Express" : numPId === 2 ? "Same Day" : numPId === 3 ? "Scheduled" : (data?.isInfo?.orderPriorityInfo?.orderPriorityName || data?.isInfo?.orderPriorityName || "");
                    return { value: numPId, label: pLabel };
                })(),
                requestedDateTimeSlot: data?.isInfo?.requestedDateTimeSlot,
                scheduledDateTimeSlot: convertUTCToLocal(data?.isInfo?.scheduledDateTimeSlot),
                invoiceNo: data.isInfo?.invoiceNo,
                orderInstruction: data.isInfo?.orderInstruction,
                orderNo: data.isInfo?.orderNo,
                orderProcessingAreaRate: data.isInfo?.orderProcessingAreaRate,
                orderSalesAmount: data.isInfo?.orderSalesAmount,
                orderTotalAmount: data.isInfo?.orderTotalAmount,
                paymentMethodInfo: { value: data?.isInfo?.paymentMethodInfo?.paymentMethodId, label: data?.isInfo?.paymentMethodInfo?.description },
                orderAmount: data.isInfo?.orderAmount,
                salesOrderNo: data?.isInfo?.salesOrderNo,
                remarks: data?.isInfo?.remarks,
                merchantInfo: data?.isInfo?.merchantInfo ? {
                    value: data.isInfo.merchantInfo.merchantId,
                    label: data.isInfo.merchantInfo.merchantName
                } : (data?.isInfo?.merchantId ? {
                    value: data.isInfo.merchantId,
                    label: data.isInfo.merchantName || "Merchant"
                } : null),
                orderZoneId: { value: data?.isInfo?.orderZoneId, label: data?.isInfo?.orderZoneName },
                collectionPointId: { value: data?.isInfo?.collectionPointId, label: data.isInfo?.collectionPointName },
                dropOffRequiredLater: data?.isInfo?.dropOffRequiredLater,
                toAddressName: { value: data?.isInfo?.fromUniqueId, label: data?.isInfo?.toAddressName } || { value: data?.isInfo?.toUniqueId, label: data?.isInfo?.toAddressName },
                toAddressContactNumber: data.isInfo?.toAddressContactNumber,
                toAddressDoorNo: data.isInfo?.toAddressDoorNo,
                toAddressPostalCode: data.isInfo?.toAddressPostalCode,
                toAddressContactPersonName: data.isInfo?.toAddressContactPersonName,
                toAddress: data.isInfo?.toAddress,
                toAddressBuildingName: data.isInfo?.toAddressBuildingName,
                toAddressAreaName: data.isInfo?.toAddressAreaName,
                buildingName: data.isInfo?.buildingName,
                toAddressFloorNo: data.isInfo?.toAddressFloorNo,
                toAddressShortCode: data.isInfo?.toAddressShortCode,
                toAddressGrlCode: data.isInfo?.toAddressGrlCode,
                longitude: data.isInfo?.longitude,
                latitude: data.isInfo?.latitude
            }
        }
    }
    return {
        initialValues: enabledTypes.length === 1 ? { orderType: enabledTypes[0] } : {}
    };
}

export default connect(mapStateToProps)(reduxForm({
    form: "orderForm",
    enableReinitialize: true,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
})(OrderAddOrUpdateForm));
